import { useNavigate } from 'react-router-dom';
import { useGetClientPartyQuery } from '../../features/platform/platformApi';

/* ------------------------------------------------------------------ *
 * Client Admin — Party hierarchy.
 * The client's own party structure (GET /platform/client/party/), private to
 * the org. Shows the levels + node tree summary, or a prompt to set it up.
 * ------------------------------------------------------------------ */

export default function ClientPartyPage() {
  const navigate = useNavigate();
  const { data: party, isLoading } = useGetClientPartyQuery();
  const has = party?.has_hierarchy ?? false;
  const levelNames = (party?.levels ?? [])
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((l) => (l.level_name as string) || 'Level');
  const rootCount = party?.tree?.length ?? 0;

  return (
    <div className="flex flex-col gap-[18px]">
      <section className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-brand">CLIENT SETUP · YOURS ONLY · NEVER SHARED</span>
        <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">Party hierarchy</h1>
        <p className="max-w-[780px] text-sm leading-6 text-ink-secondary">
          Your party structure, built on ECI booths. It stays private to your organisation, and still
          resolves against the government hierarchies through booth-to-village links.
        </p>
      </section>

      {isLoading ? (
        <section className="rounded-xl border border-line/30 bg-white p-5 text-[13px] text-ink-secondary shadow-card">
          Loading party hierarchy…
        </section>
      ) : !has ? (
        <section className="flex flex-col items-start gap-2.5 rounded-xl border border-line/30 bg-white p-5 shadow-card">
          <span className="text-[13px] text-ink-secondary">
            No party hierarchy yet. Setup takes four steps: pick a starting structure, name levels and
            posts, create units from booth order, review.
          </span>
          <button
            type="button"
            onClick={() => navigate('/client/party/setup')}
            className="h-11 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            Set up party hierarchy
          </button>
        </section>
      ) : (
        <section className="flex flex-col items-start gap-2 rounded-xl border border-line/30 bg-white p-5 shadow-card">
          <b className="text-[15px] text-ink">{levelNames.join(' › ') || 'Party hierarchy'}</b>
          <span className="text-[13px] text-ink-secondary">
            {levelNames.length} level{levelNames.length === 1 ? '' : 's'} ·{' '}
            {rootCount} top-level unit{rootCount === 1 ? '' : 's'} · private to your organisation
          </span>
          <button
            type="button"
            onClick={() => navigate('/client/party/setup')}
            className="h-11 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            Open in builder
          </button>
        </section>
      )}
    </div>
  );
}
